import PropTypes from 'prop-types';
import { UnstyledButton, Avatar, Box, Text, ActionIcon, Group } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { getNotificationConfig, formatRelativeTime } from 'utils/notification-helpers';
import { semanticColors } from 'theme';

const NotificationItem = ({ notification, onClick, onDelete }) => {
  const { icon: Icon, color } = getNotificationConfig(notification.type, notification.priority);

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(notification.id);
  };

  return (
    <UnstyledButton
      onClick={() => onClick(notification)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '14px 20px',
        backgroundColor: notification.is_read ? 'transparent' : 'rgba(240, 87, 118, 0.04)',
        borderBottom: `1px solid ${semanticColors.border.default}`,
        width: '100%'
      }}
      styles={{
        root: {
          '&:hover': {
            backgroundColor: notification.is_read ? 'rgba(0,0,0,0.04)' : 'rgba(240, 87, 118, 0.09)'
          },
          '&:hover .notif-delete': {
            opacity: 1
          }
        }
      }}
    >
      <Avatar
        size={42}
        radius="xl"
        style={{
          backgroundColor: `${color}12`,
          flexShrink: 0,
          marginTop: 2
        }}
      >
        <Icon size={22} color={color} stroke={1.5} />
      </Avatar>

      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text
          fw={notification.is_read ? 500 : 700}
          size="sm"
          lh={1.45}
          style={{ letterSpacing: '-0.005em' }}
          c={semanticColors.text.primary}
        >
          {notification.title}
        </Text>
        <Text size="xs" lh={1.5} c={semanticColors.text.secondary} mt={4} lineClamp={2}>
          {notification.message}
        </Text>
        <Text
          size="xs"
          fw={500}
          mt={6}
          c={notification.is_read ? semanticColors.text.muted : 'coral.6'}
          style={{ letterSpacing: '0.02em', fontSize: '0.7rem' }}
        >
          {formatRelativeTime(notification.created_at)}
        </Text>
      </Box>

      <Group gap={4} style={{ flexShrink: 0, marginTop: 2 }} wrap="nowrap">
        {!notification.is_read && (
          <Box
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: 'var(--mantine-color-coral-6)'
            }}
          />
        )}
        <ActionIcon
          className="notif-delete"
          size="sm"
          variant="subtle"
          color="gray"
          onClick={handleDelete}
          aria-label="Delete notification"
          style={{
            opacity: 0,
            transition: 'opacity 0.15s'
          }}
        >
          <IconX size={16} />
        </ActionIcon>
      </Group>
    </UnstyledButton>
  );
};

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    priority: PropTypes.oneOf(['info', 'success', 'urgent']).isRequired,
    resource_type: PropTypes.string,
    resource_id: PropTypes.number,
    is_read: PropTypes.bool.isRequired,
    created_at: PropTypes.string.isRequired
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

export default NotificationItem;
